import { IncomingMessage } from 'http';
import { ABTestCases } from '../../@packages/shared';
import { ABTest, MultipleABTestConfiguration, ABTestGroup } from './types';

const COOKIE_SEPARATOR = '|';
// We re-use cookie name from previous version to avoid privacy issues
export const SAVED_TRAFFIC_SLOT_COOKIE = 'savedDistributionFactor';
export const NO_AB_TEST_CONFIG_COOKIE_NAME = 'ab_test_no_ab_test_default%';
export const NO_AB_TEST_CONFIG_COOKIE_GROUP_VALUE = '%default%';

// Overridable in config;
const MAX_GROUP_NUMBER = 12;
const MAX_PARALLEL_AB_TESTS = 20;

export interface ABTestTrafficSlotDescription {
  cookieName: string;
  cookieValue: string;
  variant: string;
  title: string;
  config: Partial<ABTestCases>;
}

function maxTrafficSlotsPerConfig(configuration: MultipleABTestConfiguration) {
  const maxAbTestNumber = configuration.maxABTestNumber || MAX_PARALLEL_AB_TESTS;
  const maxGroupsPerTest = configuration.groupsPerTest || MAX_GROUP_NUMBER;
  const maxTrafficSlots = maxAbTestNumber * maxGroupsPerTest;
  return {
    maxAbTestNumber,
    maxGroupsPerTest,
    maxTrafficSlots,
  };
}

export function extractSavedSlotNumberFromRequest(
  req: IncomingMessage,
  configuration: MultipleABTestConfiguration,
): number | null {
  const { maxAbTestNumber, maxGroupsPerTest, maxTrafficSlots } = maxTrafficSlotsPerConfig(configuration);
  const savedSlotCookie = req.cookies?.[SAVED_TRAFFIC_SLOT_COOKIE];
  if (!savedSlotCookie) {
    return null;
  }
  const rawParsedCookie = savedSlotCookie.split(COOKIE_SEPARATOR).map((num) => parseInt(num, 10));
  if (rawParsedCookie.includes(NaN)) {
    return null;
  }
  const [maxAbTestNumberSaved, maxGroupsPerTestSaved, savedTrafficSlot] = rawParsedCookie;
  if (
    maxAbTestNumberSaved !== maxAbTestNumber ||
    maxGroupsPerTestSaved !== maxGroupsPerTest ||
    savedTrafficSlot < 1 ||
    savedTrafficSlot > maxTrafficSlots
  ) {
    return null;
  }
  return savedTrafficSlot;
}

export function createCookieConfig(cookieName: string, cookieValue: string, httpsOnly: boolean) {
  const options = {
    expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30),
    httpsOnly,
    secure: httpsOnly,
    path: '/',
  };
  return {
    options,
    name: cookieName,
    value: cookieValue,
  };
}

export function defaultABTestCookieConfig(slotNumber: number, httpsOnly: boolean) {
  return createCookieConfig(
    NO_AB_TEST_CONFIG_COOKIE_NAME,
    `${NO_AB_TEST_CONFIG_COOKIE_NAME}${COOKIE_SEPARATOR}${NO_AB_TEST_CONFIG_COOKIE_GROUP_VALUE}${COOKIE_SEPARATOR}${slotNumber}`,
    httpsOnly,
  );
}

export function trafficSlotNumberToCookieConfig(
  slotNumber: number,
  configuration: MultipleABTestConfiguration,
  httpsOnly: boolean,
) {
  const { maxAbTestNumber, maxGroupsPerTest } = maxTrafficSlotsPerConfig(configuration);
  return createCookieConfig(
    SAVED_TRAFFIC_SLOT_COOKIE,
    `${maxAbTestNumber}${COOKIE_SEPARATOR}${maxGroupsPerTest}${COOKIE_SEPARATOR}${slotNumber}`,
    httpsOnly,
  );
}

let trafficSlotCounter = 1;
export function resetTrafficSlotNumberGenerator() {
  trafficSlotCounter = 1;
}
// This generator is initialized multiple times in middleware
export function generateTrafficSlotNumber(configuration: MultipleABTestConfiguration) {
  const value = trafficSlotCounter++;
  if (trafficSlotCounter > maxTrafficSlotsPerConfig(configuration).maxTrafficSlots) {
    trafficSlotCounter = 1;
  }
  return value;
}

export function trafficSlotsPerAbTestGroup(abTest: ABTest, group: ABTestGroup, groupsPerTest: number) {
  const trafficSlotsPerAbTest = (abTest.slots[1] - abTest.slots[0] + 1) * groupsPerTest;
  const trafficSlotsPerGropSlot = trafficSlotsPerAbTest / groupsPerTest;
  const firstTrafficSlotInTest = (abTest.slots[0] - 1) * groupsPerTest + 1;
  const groupMinTrafficSlotNumber = (group.slots[0] - 1) * trafficSlotsPerGropSlot + firstTrafficSlotInTest;
  const groupMaxTrafficSlotNumber = group.slots[1] * trafficSlotsPerGropSlot + firstTrafficSlotInTest - 1;
  return [groupMinTrafficSlotNumber, groupMaxTrafficSlotNumber];
}

export function convertConfigToTrafficSlotAndCookieMap(
  config: MultipleABTestConfiguration,
): Map<number, ABTestTrafficSlotDescription> {
  const groupsPerTest = config.groupsPerTest || MAX_GROUP_NUMBER;
  const maxParallelAbTests = config.maxABTestNumber || MAX_PARALLEL_AB_TESTS;
  return (config.tests || []).reduce((acc, abTest) => {
    if (abTest.slots[0] > MAX_PARALLEL_AB_TESTS || abTest.slots[1] > MAX_PARALLEL_AB_TESTS) {
      console.error(
        `Skipping malformed AB test ${abTest.title}, because it occupies AB test slots ${JSON.stringify(
          abTest.slots,
        )} of ${maxParallelAbTests}`,
      );
      return acc;
    }
    (abTest.groups || []).forEach((group) => {
      if (group.slots[0] > groupsPerTest || group.slots[1] > groupsPerTest) {
        console.error(
          `Skipping malformed AB test group ${abTest.title} - ${
            group.title
          }, because it occupies group slots ${JSON.stringify(group.slots)} of ${groupsPerTest}`,
        );
      }
      const [groupMinSlotNumber, groupMaxSlotNumber] = trafficSlotsPerAbTestGroup(abTest, group, groupsPerTest);
      for (let trafficSlotNumber = groupMinSlotNumber; trafficSlotNumber <= groupMaxSlotNumber; trafficSlotNumber++) {
        acc.set(trafficSlotNumber, {
          variant: group.title,
          title: abTest.title,
          cookieName: abTest.title,
          cookieValue: `${abTest.title}${COOKIE_SEPARATOR}${group.title}${COOKIE_SEPARATOR}${trafficSlotNumber}`,
          config: group.config,
        });
      }
    });
    return acc;
  }, new Map());
}
